"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";

import { clearAllFilters } from "~/lib/storefront/filter-params";
import { cn } from "~/lib/utils";
import type { FacetGroupModel } from "./facet-model";
import { FacetGroups } from "./facet-groups";
import { createFilterHandlers } from "./filter-handlers";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-11 · Desktop filter rail. Applies live: every change pushes the URL
 * (App-Router refetch) inside a transition so the grid can dim while the
 * server re-renders. Hidden below the PLP breakpoint (the drawer takes over).
 */
export function FilterRail({
  groups,
  currency,
  hasActive,
}: {
  groups: FacetGroupModel[];
  currency: string;
  hasActive: boolean;
}) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const search = params.toString();

  const push = (next: string) =>
    startTransition(() =>
      router.push(next ? `${pathname}?${next}` : pathname, { scroll: false }),
    );
  const handlers = createFilterHandlers(search, push, currency);

  if (groups.length === 0) return null;

  return (
    <aside
      aria-label={t("browse.filters")}
      className={cn(
        "hidden w-60 shrink-0 lg:block",
        isPending && "pointer-events-none opacity-60",
      )}
    >
      <div className="flex items-center justify-between pb-3">
        <h2 className="font-heading text-lg font-semibold text-fg">
          {t("browse.filters")}
        </h2>
        {hasActive && (
          <button
            type="button"
            onClick={() => {
              push(clearAllFilters(search));
            }}
            className="text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {t("browse.clearAll")}
          </button>
        )}
      </div>
      <FacetGroups groups={groups} handlers={handlers} />
    </aside>
  );
}
